import React, { useState, useEffect, useCallback } from 'react';
import {useAdminTheme} from '@/contexts/AdminThemeContext';import {Lock, Unlock, ShieldAlert, Loader2, RefreshCw, Clock, AlertTriangle, Mail} from 'lucide-react';import {Button} from '@/components/ui/button';import {toast} from 'sonner';import AdminLayout from './AdminLayout';
import axios from 'axios';
import {API_URL as API} from '@/utils/apiConfig';

export default function AdminLockedUsersPage() {
  const { isDark, colors } = useAdminTheme();
  const [locked, setLocked] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLocked = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/locked-users`, { withCredentials: true });
      setLocked(res.data.locked_users || []);
    } catch {
      toast.error('Failed to load locked users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLocked(); }, [fetchLocked]);

  const handleUnlock = async (userId, email) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/unlock`, {}, { withCredentials: true });
      toast.success(`Unlocked ${email}`);
      fetchLocked();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to unlock');
    }
  };

  const handleSendReset = async (userId, email) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/send-password-reset`, {}, { withCredentials: true });
      toast.success(`Password reset email sent to ${email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send reset');
    }
  };

  const permanent = locked.filter(u => u.permanently_locked);
  const temporary = locked.filter(u => !u.permanently_locked);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-locked-users-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-5 w-5 text-red-400" />
            <h1 className="text-xl font-bold" style={{ color: colors.text }}>Locked Accounts</h1>
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 font-medium">{locked.length}</span>
          </div>
          <Button onClick={fetchLocked} variant="ghost" size="sm" className="gap-1.5 text-xs" style={{ color: colors.textSecondary }}>
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.textMuted }} /></div>
        ) : locked.length === 0 ? (
          <div className="rounded-2xl p-12 text-center" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }}>
            <Lock className="h-8 w-8 mx-auto mb-3" style={{ color: colors.textMuted }} />
            <p className="text-sm" style={{ color: colors.textMuted }}>No locked accounts</p>
          </div>
        ) : (
          <>
            {permanent.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-400" />
                  <h2 className="text-sm font-semibold text-red-400">Permanently Locked ({permanent.length})</h2>
                </div>
                <div className="space-y-2">
                  {permanent.map(u => (
                    <LockedUserCard key={u.email} user={u} colors={colors} isDark={isDark}
                      onUnlock={() => handleUnlock(u.user_id, u.email)}
                      onSendReset={() => handleSendReset(u.user_id, u.email)} />
                  ))}
                </div>
              </div>
            )}

            {temporary.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-400" />
                  <h2 className="text-sm font-semibold text-amber-400">Temporarily Locked ({temporary.length})</h2>
                </div>
                <div className="space-y-2">
                  {temporary.map(u => (
                    <LockedUserCard key={u.email} user={u} colors={colors} isDark={isDark}
                      onUnlock={() => handleUnlock(u.user_id, u.email)}
                      onSendReset={() => handleSendReset(u.user_id, u.email)} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function LockedUserCard({ user, colors, isDark, onUnlock, onSendReset }) {
  const isPermanent = user.permanently_locked;

  return (
    <div
      className="rounded-xl p-4 flex items-center justify-between gap-4"
      style={{
        background: colors.card,
        border: `1px solid ${isPermanent ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`,
      }}
      data-testid={`locked-user-${user.email}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm truncate" style={{ color: colors.text }}>{user.name || 'Unknown'}</p>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${isPermanent ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
            {isPermanent ? 'Permanent' : 'Temp'}
          </span>
        </div>
        <p className="text-xs truncate mt-0.5" style={{ color: colors.textMuted }}>{user.email}</p>
        <div className="flex items-center gap-3 mt-1.5 text-[11px]" style={{ color: colors.textMuted }}>
          <span>Attempts: {user.attempts}</span>
          <span>Phase: {user.phase}</span>
          {user.locked_until && !isPermanent && (
            <span>Unlocks: {new Date(user.locked_until).toLocaleTimeString()}</span>
          )}
          {user.locked_at && <span>Locked: {new Date(user.locked_at).toLocaleString()}</span>}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-3 text-xs gap-1.5 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
          onClick={onUnlock}
          data-testid={`unlock-btn-${user.email}`}
        >
          <Unlock className="h-3.5 w-3.5" /> Unlock
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-3 text-xs gap-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
          onClick={onSendReset}
          data-testid={`reset-btn-${user.email}`}
        >
          <Mail className="h-3.5 w-3.5" /> Send Reset
        </Button>
      </div>
    </div>
  );
}
